import { useEffect, useRef, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  useToast,
  Text,
  FormLabel,
  FormControl,
  InputGroup,
  InputLeftAddon,
  Button,
  Spinner,
} from "@chakra-ui/react";
import { BrandButton } from "../BrandButton/BrandButton";
import { getAxiosClient } from "../../services/HttpsService";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/services/state/store";
import { useParams } from "react-router-dom";
import { fetchFoldersByApplication } from "../../services/state/folders/folders-async-calls";

export const AddFolderModal = ({
  isOpen,
  onClose,
  edit = false,
  tenant,
}: {
  isOpen: boolean;
  onClose: () => void;
  edit?: boolean;
  tenant?: any;
}) => {
  const initialRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const { applicationId } = useParams();

  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false); // Add loading state

  const selectedApplication = useSelector(
    (state: RootState) => state.applications.selectedApplication
  );

  useEffect(() => {
    if (edit && tenant) {
      setName(tenant.name);
      setType(tenant.iconUri);
    }
  }, [edit, tenant]);

  const onCreate = async () => {
    if (name !== "") {
      setLoading(true); // Set loading to true when the request starts
      const url = "/api/eza_app_folder";
      const data = {
        folderNm: selectedApplication?.appPrefix
          ? selectedApplication.appPrefix + "_" + name
          : name,
        folderTyp: type,
        appUuid: applicationId,
      };

      try {
        const response = await getAxiosClient().post(url, data);

        if (response.status === 201) {
          toast({
            title: "Successfully created new folder",
            description: "Created new folder with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchFoldersByApplication(applicationId as string));
          setName("");
          setType("");
        } else {
          toast({
            title: "Some error has occured",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error creating folder:", error);
        toast({
          title: "Error",
          description: "An error occurred while creating new folder.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false); // Reset loading state after request completes
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const onUpdate = async () => {
    if (name !== "") {
      setLoading(true); // Set loading to true when the request starts
      const url = `/tenant/${tenant?.id}`;
      const data = {
        appDesc: type,
        isActive: true,
      };
      try {
        const response = await getAxiosClient().put(url, data);
        if (response.status === 202) {
          toast({
            title: "Successfully updated application",
            description: "Updated application with entered details",
            position: "top",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
          onClose();
          dispatch(fetchFoldersByApplication(applicationId as string));
          setName("");
          setType("");
        } else {
          toast({
            title: "Some error has occured",
            description: "Please try again",
            position: "top",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        }
      } catch (error) {
        console.error("Error updating application:", error);
        toast({
          title: "Error",
          description: "An error occurred while updating new application.",
          position: "top",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setLoading(false); // Reset loading state after request completes
      }
    } else {
      toast({
        title: "Name is required",
        description: "Please enter a name and try again",
        position: "top",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      initialFocusRef={initialRef}
      size="xl"
      isCentered
    >
      <ModalOverlay
        zIndex={1}
        backdropFilter="blur(10px)" // ← This will blur the page behind the modal
        bg="rgba(0, 0, 0, 0.4)"
      />
      <ModalContent
        bg="rgba(0, 0, 0, 0.9)"
        backdropFilter="blur(20px)"
        border="1px solid rgba(255, 255, 255, 0.4)"
        borderRadius="20px"
        color="white"
      >
        <ModalCloseButton color="white" />
        <ModalBody>
          {edit ? (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Edit Folder
            </Text>
          ) : (
            <Text fontSize="20px" fontWeight="semibold" my={4} color="white">
              Create New Folder
            </Text>
          )}
          <FormControl isRequired>
            <FormLabel color="white">Name</FormLabel>
            <InputGroup>
              {selectedApplication?.appPrefix && (
                <InputLeftAddon
                  bg="rgba(255, 255, 255, 0.1)"
                  borderColor="rgba(255, 255, 255, 0.2)"
                  color="white"
                >
                  {selectedApplication.appPrefix}_
                </InputLeftAddon>
              )}
              <Input
                placeholder="Enter name of the folder"
                focusBorderColor="brand.500"
                ref={initialRef}
                fontSize="18px"
                value={name}
                onChange={(e) => setName(e.target.value)}
                overflowX="auto"
                isRequired
                disabled={edit}
                bg={
                  edit
                    ? "rgba(255, 255, 255, 0.05)"
                    : "rgba(255, 255, 255, 0.1)"
                }
                borderColor="rgba(255, 255, 255, 0.2)"
                color="white"
                _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
                _focus={{
                  borderColor: "brand.500",
                  boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
                }}
                _disabled={{
                  bg: "rgba(255, 255, 255, 0.05)",
                  color: "rgba(255, 255, 255, 0.5)",
                }}
              />
            </InputGroup>
          </FormControl>
          <FormControl mt={3}>
            <FormLabel color="white">Type</FormLabel>
            <Input
              placeholder="Enter type of the folder"
              focusBorderColor="brand.500"
              fontSize="18px"
              value={type}
              onChange={(e) => setType(e.target.value)}
              overflowX="auto"
              isRequired
              disabled={edit}
              bg={
                edit ? "rgba(255, 255, 255, 0.05)" : "rgba(255, 255, 255, 0.1)"
              }
              borderColor="rgba(255, 255, 255, 0.2)"
              color="white"
              _placeholder={{ color: "rgba(255, 255, 255, 0.6)" }}
              _focus={{
                borderColor: "brand.500",
                boxShadow: "0 0 0 1px rgba(118, 82, 247, 0.5)",
              }}
              _disabled={{
                bg: "rgba(255, 255, 255, 0.05)",
                color: "rgba(255, 255, 255, 0.5)",
              }}
            />
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <BrandButton
            onClick={edit ? onUpdate : onCreate}
            isLoading={loading} // Add loading state to button
            isDisabled={loading} // Disable button when loading
          >
            {edit ? "Save Changes" : "Create"}
          </BrandButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
